<script setup lang="ts">
import { computed, ref } from 'vue';

import Modal from '@/components/base/layout/Modal.vue';
import FTBInitialScreen from '@/components/personalization/FTBInitialScreen.vue';
import FTBSecondScreen from '@/components/personalization/FTBSecondScreen.vue';
import { useOnsiteOffer } from '@/composables/personalization/useOnsiteOffer';
import { useNotifications } from '@/stores/notifications';

const FTBAnalyticsEventName = '20% Off and Free Shipping Experience - $59';
const ExistingContactNotification = `<span class="font-semibold">Check your texts</span><br/>Reply 'Y' to confirm your subscription. Once confirmed, you'll get a link to redeem your offer!`;
const NewContactNotification = `<span class="font-semibold">Offer Claimed!</span><br/>Congrats! You're getting 20% off your purchase and free shipping when you spend $59 and more. <span class="font-semibold">Your offer will apply at checkout.</span>`;

const { setNotification } = useNotifications();

const { closeModal, config: staticConfig, rescindOffer } = useOnsiteOffer();
const config = computed(() => staticConfig.value);

const currentStep = ref<'initial' | 'secondary'>('initial');
const emailDiscountApplied = ref(false);

const emailInput = ref('');
const existingContact = ref(false);
const smsSubscribed = ref(false);

function handleEmailSignupCompleted() {
  currentStep.value = 'secondary';
  rescindOffer(false);
}

const newContactNotification = computed(() => {
  const customMessage = config.value.settings?.newContactNotification;
  return customMessage
    ? `<span class="font-semibold">Offer Claimed!</span><br/>${customMessage}`
    : NewContactNotification;
});

const existingContactNotification = computed(() => {
  const customMessage = config.value.settings?.existingContactNotification;
  return customMessage
    ? `<span class="font-semibold">Check your texts</span><br/>${customMessage}`
    : ExistingContactNotification;
});

function handleClose() {
  closeModal();

  if (emailDiscountApplied.value) {
    setNotification({
      key: 'success',
      message: newContactNotification.value,
      messageType: 'notices',
    });

    window.scrollTo(0, 0);
  }
}

function handlePhoneSignupCompleted(contactAlreadyExists: boolean) {
  closeModal();

  setNotification({
    key: 'success',
    message: contactAlreadyExists
      ? existingContactNotification.value
      : newContactNotification.value,
    messageType: 'notices',
  });

  window.scrollTo(0, 0);
}
</script>

<template>
  <Modal
    v-if="config.initialScreen"
    aria-label="New customer offer"
    fullHeight
    :hasControls="false"
    :hasDefaultPadding="false"
    :isOpen="config.isOpen"
    width="md:max-w-3xl"
    @handle-close="handleClose"
  >
    <template #body>
      <FTBInitialScreen
        v-if="currentStep === 'initial'"
        class="h-[600px]"
        :analyticsEventName="config.settings?.analyticsSource || FTBAnalyticsEventName"
        v-model:emailDiscountApplied="emailDiscountApplied"
        v-model:emailInput="emailInput"
        v-model:existingContact="existingContact"
        v-model:smsSubscribed="smsSubscribed"
        :initialScreen="config.initialScreen"
        @email-signup-completed="handleEmailSignupCompleted"
      />
      <FTBSecondScreen
        v-else-if="currentStep === 'secondary' && config.secondaryScreen"
        class="h-[600px]"
        :analyticsEventName="config.settings?.analyticsSource || FTBAnalyticsEventName"
        :emailInput
        :existingContact
        :secondaryScreen="config.secondaryScreen"
        :smsSubscribed
        @phone-signup-completed="handlePhoneSignupCompleted"
      />
    </template>
  </Modal>
</template>
